import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Media } from '@klickdata/core/media/src/media.model';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { AppScope, ResourceTag, ResourceTypes } from '@klickdata/core/resource';
import { DialogDisplayImgComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-display-img/dialog-display-img.component';
import { MatTableBase } from 'apps/klickdata/src/app/shared/table/mat-table-base';
import { filter, map, Observable, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { K3MediaListingService } from '../k3-media-listing.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageDeletedComponent, MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { TableFilterComponent } from '@klickdata/core/table';
import { User } from '@klickdata/core/user';
import { AuthService } from '@klickdata/core/auth';
import { SelectFilterOption } from '@klickdata/core/table';
import { Utils } from '@klickdata/core/util';
import { DialogVideoPlayerComponent } from '../../dialog-video-player/dialog-video-player.component';
import { MediaType } from '@klickdata/core/media/src/media-type';
import { ListingContext } from '@klickdata/core/application/src/model/model-interface';
import { MaterialModelType } from '@klickdata/core/resource/src/resource.model';
import { Clipboard } from '@angular/cdk/clipboard';

@Component({
    selector: 'app-images-listing-general',
    templateUrl: './images-listing-general.component.html',
    styleUrls: ['./images-listing-general.component.scss'],
    providers: [K3MediaListingService],
})
export class ImagesListingGeneralComponent extends MatTableBase<Media> implements AfterViewInit {
    @Output() onMediaClick: EventEmitter<Media> = new EventEmitter<Media>();
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    @Input() public columns = [
        'id',
        'thumbnil',
        'filename',
        'description',
        'tags',
        'visibility',

        'author',
        'updated_at',
        'tools',
    ];
    @Input() public hasExpansionFilter = true;
    @Input() public listingContext: ListingContext;
    @Input() public pageSize = 25;
    AppScope = AppScope;
    public authUser: User;
    public filePrioOptions: SelectFilterOption[];

    constructor(
        protected listService: K3MediaListingService,
        protected mobileService: MobileService,
        protected cdr: ChangeDetectorRef,
        protected dialog: MatDialog,
        protected route: ActivatedRoute,
        protected router: Router,
        protected message: MessageService,
        protected auth: AuthService,
        protected clipboard: Clipboard
    ) {
        super(cdr, mobileService, listService);
        this.filePrioOptions = Utils.getTaskPrioOptions();
        this.auth
            .getUser()
            .pipe(takeUntil(this.destroy))
            .subscribe((user) => {
                this.authUser = user;
            });
    }
    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.route.queryParams.pipe(filter((params) => params.action === 'addImage')).subscribe(() =>
            setTimeout(() => {
                this.addMedia();
                this.router.navigate([this.router.url.split('?')[0]]);
            }, 500)
        );
        this.listenToMediaSocket();
    }
    public refreshMediaListing() {
        this.dataSource.refresh();
    }
    public onFilterByFavorite(filter: boolean) {
        if (filter) {
            this.filter.createOrUpdateFilter([{ property: 'favorite', items: [this.authUser.id] }]);
        } else {
            this.filter.ignoreFilter(['favorite']);
        }
    }
    public addMedia() {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.AI_CREATE_UPDATE_MEDIA,
            data: {
                type: 'addImage',
                aiModelType: MaterialModelType.IMAGE,
                title: $localize`Add a new image`,
                icon: 'image',
                uploadBtnText: $localize`Upload an image`,
                aiBtnText: $localize`Create an image with AI`,
                allowedMimeType: 'image/png, image/jpg, image/jpeg',
                aiPromptPlaceholder: $localize`Write a prompt and let AI create an image`,
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                tap(() => this.router.navigate([], { queryParams: null })),
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'addImage'),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                this.dataSource.refresh();
            });
    }
    public editMediaImg(media: Media) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.AI_CREATE_UPDATE_MEDIA,
            data: {
                type: 'editImage',
                aiModelType: MaterialModelType.IMAGE,
                title: $localize`Edit image`,
                icon: 'image',
                uploadBtnText: $localize`Upload an image`,
                aiBtnText: $localize`Create an image with AI`,
                allowedMimeType: 'image/png, image/jpg, image/jpeg',
                aiPromptPlaceholder: $localize`Write a prompt and let AI create an image`,
                media: media,
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                tap(() => this.router.navigate([], { queryParams: null })),
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'editImage'),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                this.dataSource.refresh();
                this.cdr.markForCheck();
            });
    }
    public addMediaItem() {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_IMAGE_TO_LIST,
            data: {
                type: 'addImage',
                mediaType: $localize`Image`,
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'addImage'),
                takeUntil(this.destroy),
                map((data) => data.value)
            )
            .subscribe((medias) => {
                this.dataSource.replace(medias.map((media) => new Media(media)));
                this.cdr.markForCheck();
            });
    }
    public listenToMediaSocket() {
        this.listService
            .listenToMediaEvent({ type: MaterialModelType.IMAGE })
            .pipe(
                takeUntil(this.destroy),
                filter((media) => media.listingContext === this.listingContext)
            )
            .subscribe((media) => {
                this.dataSource.replace(media);
            });
    }
    public createMaterial(media: Media) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.CREATE_RESOURCE,
            data: {
                typeId: ResourceTypes.DocMaterial,
                items: [{ name: media.path, item_type_value: media.type }],
            },
        });
    }
    public edit(media: Media) {
        this.editMediaImg(media);
        // this.openImageEditor(media).subscribe(() => this.dataSource.refresh());
    }
    public favorite(media: Media) {
        media.favorite = !media.favorite;
        this.listService
            .updateMedia(+media.id, { favorite: media.favorite }, 'tags,user_feeds')
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                this.dataSource.refresh();
                this.message.openMessage(MessageSavedComponent, $localize`Favorite updated`);
            });
    }

    public delete(id: number) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete Image`,
                contentBody: $localize`You are about to delete this image. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap((res) => {
                    if (res) {
                        return this.listService.destroy(id);
                    }
                })
            )
            .subscribe((action) => {
                if (action) {
                    this.message.openMessage(MessageDeletedComponent);
                    this.dataSource.refresh();
                }
            });
    }
    public view(media: Media) {
        if (media.mediaType == MediaType.VIDEO) {
            this.dialog.open(DialogVideoPlayerComponent, {
                maxWidth: '60%',
                data: {
                    id: media.id,
                    autoplay: false,
                },
            });
        } else {
            this.dialog
                .open(DialogDisplayImgComponent, {
                    maxWidth: this.isMobile ? '90%' : '70%',
                    maxHeight: '90vh',
                    data: media,
                })
                .afterClosed()
                .pipe(
                    switchMap((result) => (result?.type === 'edit' ? this.openImageEditor(result?.media) : of(result))),
                    takeUntil(this.destroy)
                )
                .subscribe(() => this.refreshMediaListing());
        }
    }
    onTagClick(tag: ResourceTag | any) {
        this.filter.filterByModel(new ResourceTag(tag).getFilter(), true);
    }
    public updateMediaTags(media: Media, tagsIds: number[]) {
        this.listService.updateMedia(+media.id, { tag_ids: tagsIds }, 'tags').pipe(takeUntil(this.destroy)).subscribe();
    }
    onSelectPriority(media: Media, prio: string) {
        this.listService
            .updateMedia(+media.id, { priority: prio })
            .pipe(
                takeUntil(this.destroy),
                tap(() => this.dataSource.refresh())
            )
            .subscribe();
    }
    public copyLink(link: string) {
        this.clipboard.copy(link);
        this.message.openMessage(MessageSavedComponent, $localize`Link copied to clipboard`);
    }
    private openImageEditor(media?: Media): Observable<unknown> {
        if (!media) {
            return of(null);
        }
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_IMAGE_TO_LIST,
            data: {
                type: 'editImage',
                mediaType: $localize`Image`,
                media,
            },
        });
        return this.mobileService.getSideNavAction().pipe(
            filter((action) => action === SideNaveActionsTypes.POSITIVE),
            switchMap(() => this.mobileService.getSideNavResponseData()),
            filter((data) => data.type === 'editImage'),
            takeUntil(this.destroy)
        );
    }
}
